import React, { useState, useRef, useMemo, useEffect } from 'react';
import JoditEditor from 'jodit-react';

const MyEditor = ({ initialContent, onSave, onCancel }) => {
    const editor = useRef(null);
    const [content, setContent] = useState(initialContent);
    const [showEditor, setShowEditor] = useState(true);
    const [wordCount, setWordCount] = useState(0);

    const MAX_WORDS = 1500;

    const changeFontSize = (direction) => {
        if (!editor.current || !editor.current.editor) return;

        const editorRoot = editor.current.editor;
        // Get all block-level elements that contain text
        const elements = editorRoot.querySelectorAll('p, div, h1, h2, h3, h4, h5, h6, li, span');
        const step = 2; // Increase or decrease by 2px

        elements.forEach(el => {
            const currentSize = parseFloat(window.getComputedStyle(el).fontSize);
            let newSize = currentSize;

            if (direction === 'increase') {
                newSize = currentSize + step;
            } else if (direction === 'decrease') {
                newSize = currentSize - step;
            }

            // Apply a minimum size to prevent disappearing text
            if (newSize < 8) newSize = 8;

            el.style.fontSize = `${newSize}px`;
        });

        // Update the component's state to reflect the change
        setContent(editorRoot.innerHTML);
    };

    // Configuration for Jodit Editor
    const config = useMemo(() => ({
        readonly: false,
        buttons: 'paragraph,bold,italic,underline,strikethrough,superscript,subscript,ul,ol,font,fontsize,lineheight,align,undo,redo,source',
        toolbarButtonSize: 'large',
        allowResize: false,
        height: '500px',
        editorCssClass: 'jodit-align-left',
        placeholder: 'Start typing...',
        defaultMode: '1',
        askBeforePasteHTML: false,
        askBeforePasteFromWord: false,
    }), []);

    // Update word count whenever content changes
    useEffect(() => {
        const plainText = content.replace(/<[^>]*>/g, '').trim();
        const words = plainText.split(/\s+/).filter(Boolean);
        setWordCount(words.length);
    }, [content]);

    // Handle Save and Cancel button clicks
    const handleSaveClick = () => showConfirmationDialog('save');
    const handleCancelClick = () => showConfirmationDialog('cancel');

    // Dynamically create the confirmation dialog
    const showConfirmationDialog = (action) => {
        const existingDialog = document.querySelector('.customConfirmDialog');
        if (existingDialog) {
            existingDialog.remove();
        }
        const dialogElement = document.createElement('div');
        dialogElement.classList.add('customConfirmDialog');
        dialogElement.innerHTML = `
            <div class="confirm-dialog-content">
                <p id="confirmMessage">${action === 'save' ? 'Do you want to save your changes?' : 'Are you sure you want to cancel your changes?'}</p>
                <button class="confirmBtn">Yes</button>
                <button class="cancelBtn">No</button>
            </div>
        `;
        document.body.appendChild(dialogElement);

        // Position and style the dialog
        dialogElement.style.position = 'fixed';
        dialogElement.style.top = '50%';
        dialogElement.style.left = '50%';
        dialogElement.style.minWidth = '300px';
        dialogElement.style.transform = 'translate(-50%, -50%)';
        dialogElement.style.zIndex = 1001;

        // Add event listeners
        const confirmBtn = dialogElement.querySelector('.confirmBtn');
        const cancelBtn = dialogElement.querySelector('.cancelBtn');
        confirmBtn.onclick = () => handleConfirm(action);
        cancelBtn.onclick = handleCancelDialog;
    };

    // Handle the "Yes" button click
    const handleConfirm = (action) => {
        if (action === 'save') {
            const html = editor.current.value;

            if (typeof onSave === 'function') {
                onSave(html);
            }

            setShowEditor(false);
        }

        if (action === 'cancel') {
            onCancel?.();
            setShowEditor(false);
        }

        closeDialog();
    };


    // Handle the "No" button click
    const handleCancelDialog = () => closeDialog();

    // Close the dialog
    const closeDialog = () => {
        const dialogElement = document.querySelector('.customConfirmDialog');
        if (dialogElement) {
            dialogElement.remove();
        }
    };

    const showWordsLimitDialog = () => {
        const existingDialog = document.querySelector('.customWordsLimitDialog');
        if (existingDialog) {
            existingDialog.remove();
        }
        const dialogElement = document.createElement('div');
        dialogElement.classList.add('customWordsLimitDialog');
        dialogElement.innerHTML = `
            <div class="confirm-dialog-content">
                <p>Words Limit Exceeded</p>
                <button class="okBtn">OK</button>
            </div>
        `;
        document.body.appendChild(dialogElement);
        dialogElement.style.position = 'fixed';
        dialogElement.style.top = '50%';
        dialogElement.style.left = '50%';
        dialogElement.style.transform = 'translate(-50%, -50%)';
        dialogElement.style.zIndex = 1001;

        const okBtn = dialogElement.querySelector('.okBtn');
        okBtn.onclick = () => dialogElement.remove();
    };

    return (
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                display: showEditor ? 'flex' : 'none',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 900,
            }}
        >
            <div
                style={{
                    width: '90%',
                    maxWidth: '600px',
                    height: '80%',
                    maxHeight: '500px',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    textAlign: 'left',
                }}
            >
                <JoditEditor
                    ref={editor}
                    config={config}
                    value={content}
                    tabIndex={1}

                    // Live updates (safe)
                    onChange={(newContent) => {
                        const plainText = newContent.replace(/<[^>]*>/g, '').trim();
                        const words = plainText.split(/\s+/).filter(Boolean);
                        setWordCount(words.length);
                    }}

                    // Save content only when leaving editor
                    onBlur={(newContent) => {
                        const plainText = newContent.replace(/<[^>]*>/g, '').trim();
                        const words = plainText.split(/\s+/).filter(Boolean);

                        if (words.length > MAX_WORDS) {
                            showWordsLimitDialog();
                            return;
                        }

                        setContent(newContent);
                    }}
                />



                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '10px',
                        backgroundColor: '#f1f1f1',
                        borderTop: '1px solid #ddd',
                        marginTop: '-25px',
                        flexWrap: 'wrap',
                    }}
                >
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button
                            onClick={() => changeFontSize('decrease')}
                            style={{ padding: '0 5px', cursor: 'pointer', fontWeight: "bold", backgroundColor: 'white', border: '1px solid #ddd', borderRadius: '5px' }}
                            title="Decrease font size"
                        >
                            <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
                                <path d="M6 11h12v2H6z" />
                            </svg>
                        </button>
                        <button
                            onClick={() => changeFontSize('increase')}
                            style={{ padding: '0 5px', cursor: 'pointer', fontWeight: "bold", backgroundColor: 'white', border: '1px solid #ddd', borderRadius: '5px' }}
                            title="Increase font size"
                        >
                            <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
                                <path d="M11 5h2v6h6v2h-6v6h-2v-6H5v-2h6z" />
                            </svg>
                        </button>
                    </div>
                    <span style={{ textAlign: 'left', padding: '5px', whiteSpace: 'nowrap' }}>{wordCount}/{MAX_WORDS} words</span>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button
                            onClick={handleSaveClick}
                            style={{ padding: '10px', cursor: 'pointer', backgroundColor: 'lightgreen', fontWeight: "bold" }}
                        >
                            Save
                        </button>
                        <button
                            onClick={handleCancelClick}
                            style={{ padding: '10px', cursor: 'pointer', backgroundColor: 'red', fontWeight: "bold" }}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MyEditor;
